import { makeAutoObservable } from "mobx";

import type { Vec2Type } from "models-webgl2";
import { MathPI2 } from "project-constants/misc.constants";
import { normalizeClamped } from "../normalize-clamped";
import { lerpRounded } from "../lerp-rounded";
import { clamp } from "../clamp";
import { isWithinRange } from "../is-within-range";
import { toDegrees } from "../to-degrees";
import { toRadians } from "../to-radians";
import { negation } from "../negation";
import { round } from "../round";
import { roundToClosest } from "../round-to-closest";
import { isPositive as isPositiveUtil } from "../is-positive";

export class Vec2 {
  private readonly _vector: Vec2Type;

  public get x(): number {
    return this._vector[0];
  }

  public get width(): number {
    return this._vector[0];
  }

  public get y(): number {
    return this._vector[1];
  }

  public get height(): number {
    return this._vector[1];
  }

  private _animationFrameId = 0;
  private _startTime = 0;

  constructor(x: number, y: number) {
    this._vector = [x, y];
    makeAutoObservable(this);
  }

  /* Computed utility values ↓ */

  // (Abscissa / Ordinate) ratio
  public get ratioAO() {
    return this.x / this.y;
  }

  // (Ordinate / Abscissa) ratio
  public get ratioOA() {
    return this.y / this.x;
  }

  public length() {
    return Math.hypot(this.x, this.y);
  }

  public equals(x: number, y: number): boolean;
  public equals(vec: Vec2): boolean;
  public equals(a: any, b?: any) {
    const isVec = a instanceof Vec2;
    const x = isVec ? a.x : a;
    const y = isVec ? a.y : b;
    return this.x === x && this.y === y;
  }

  public isZero() {
    return !this.x && !this.y;
  }

  public hasZero() {
    return !this.x || !this.y;
  }

  public isPositive() {
    return isPositiveUtil(this.x) && isPositiveUtil(this.y);
  }

  public isNegative() {
    return !this.isPositive();
  }

  public isWithin(v1: Vec2, v2: Vec2) {
    return isWithinRange(this.x, v1.x, v2.x) && isWithinRange(this.y, v1.y, v2.y);
  }

  public min() {
    return Math.min(this.x, this.y);
  }

  public max() {
    return Math.max(this.x, this.y);
  }

  public distanceTo(vec: Vec2) {
    return Math.hypot(vec.x - this.x, vec.y - this.y);
  }

  public getArray(): Vec2Type {
    return [this.x, this.y];
  }

  public getRoundX(decimalPoint?: number) {
    return round(this.x, decimalPoint);
  }

  public getRoundY(decimalPoint?: number) {
    return round(this.y, decimalPoint);
  }

  public getAbsX() {
    return Math.abs(this.x);
  }

  public getAbsY() {
    return Math.abs(this.y);
  }
  public getCeilX() {
    return Math.ceil(this.x);
  }

  public getCeilY() {
    return Math.ceil(this.y);
  }
  public getFloorX() {
    return Math.floor(this.x);
  }

  public getFloorY() {
    return Math.floor(this.y);
  }

  public getSignX(zeroValue = 0) {
    return Math.sign(this.x) || zeroValue;
  }

  public getSignY(zeroValue = 0) {
    return Math.sign(this.y) || zeroValue;
  }

  public dot(vec: Vec2): number {
    return this.x * vec.x + this.y * vec.y;
  }

  public cross(vec: Vec2): number {
    return this.x * vec.y - this.y * vec.x;
  }

  // angle between vectors in radians (can't be more than Math.PI)
  public rads(vec: Vec2): number {
    const numerator = this.x * vec.x + this.y * vec.y;
    const mag = this.length() * vec.length();
    const cosine = mag && numerator / mag; // mag &&.. short circuits if mag == 0
    return Math.acos(cosine);
  }

  // angle between vectors in degrees (can't be more than 180deg)
  public degrees(vec: Vec2): number {
    return toDegrees(this.rads(vec));
  }

  // angle according unit circle
  public unitRads(): number {
    return (MathPI2 + Math.atan2(this.y, this.x)) % MathPI2;
  }

  // angle according unit circle
  public unitDegrees(): number {
    return toDegrees(this.unitRads());
  }

  public quarterToRads(origin: Vec2, axisRads = 0): 0 | 1 | 2 | 3 | 4 {
    if (this.equals(origin)) return 0;

    const { x, y } = this.clone().rotate(origin, -axisRads);
    if (x > origin.x && y > origin.y) return 1;
    if (x > origin.x && y < origin.y) return 4;
    if (x < origin.x && y > origin.y) return 2;
    if (x < origin.x && y < origin.y) return 3;

    return 0;
  }

  public quarterToDegrees(origin: Vec2, axisDegrees = 0): 0 | 1 | 2 | 3 | 4 {
    return this.quarterToRads(origin, toRadians(axisDegrees));
  }

  /* Computed utility values ↑ */

  /* Methods which returns new instance of Vec ↓ */

  public clone() {
    return new Vec2(this.x, this.y);
  }

  public getHalf() {
    return new Vec2(this.x / 2, this.y / 2);
  }

  public getSign(zeroValue = 0): Vec2 {
    return new Vec2(this.getSignX(zeroValue), this.getSignY(zeroValue));
  }

  public getAbs() {
    return new Vec2(this.getAbsX(), this.getAbsY());
  }

  public getCeil() {
    return new Vec2(this.getCeilX(), this.getCeilY());
  }

  public getFloor() {
    return new Vec2(this.getFloorX(), this.getFloorY());
  }

  public getRound(decimalPoint?: number) {
    return new Vec2(this.getRoundX(decimalPoint), this.getRoundY(decimalPoint));
  }

  public directionTo(vec: Vec2): Vec2 {
    return new Vec2(vec.x - this.x, vec.y - this.y);
  }

  public median(vec: Vec2) {
    return new Vec2(vec.x + this.x, vec.y + this.y).divide(2);
  }

  public moveOntoDirectionLength(direction: Vec2, length: number) {
    const lengthDiff = length / direction.length();
    const moveDistanceX = direction.x * lengthDiff;
    const moveDistanceY = direction.y * lengthDiff;
    return new Vec2(this.x + moveDistanceX, this.y + moveDistanceY);
  }

  public perpendicularOntoLine(startPoint: Vec2, endPoint: Vec2) {
    const k =
      ((endPoint.y - startPoint.y) * (this.x - startPoint.x) -
        (endPoint.x - startPoint.x) * (this.y - startPoint.y)) /
      (Math.pow(endPoint.y - startPoint.y, 2) + Math.pow(endPoint.x - startPoint.x, 2));

    const pX = this.x - k * (endPoint.y - startPoint.y);
    const pY = this.y + k * (endPoint.x - startPoint.x);

    return new Vec2(pX, pY);
  }

  public transformMat3(matrix: ArrayLike<number>) {
    const thisX = this.x;
    const thisY = this.y;

    const x = thisX * matrix[0] + thisY * matrix[3] + matrix[6];
    const y = thisX * matrix[1] + thisY * matrix[4] + matrix[7];

    return new Vec2(x, y);
  }

  public transformMat3Det(matrix: ArrayLike<number>) {
    const thisX = this.x;
    const thisY = this.y;

    const z = thisX * matrix[2] + thisY * matrix[5] + matrix[8];

    const x = (thisX * matrix[0] + thisY * matrix[3] + matrix[6]) / z;
    const y = (thisX * matrix[1] + thisY * matrix[4] + matrix[7]) / z;

    return new Vec2(x, y);
  }

  /* Methods which returns new instance of Vec2 ↑ */

  /* Methods which modify current instance of Vec2 ↓ */

  public copyX(value: number) {
    if (this._animationFrameId) return false;
    const equal = value === this.x;
    this._vector[0] = value;
    return !equal;
  }

  public copyY(value: number) {
    if (this._animationFrameId) return false;
    const equal = value === this.y;
    this._vector[1] = value;
    return !equal;
  }

  public copy(fromX: number, fromY: number): boolean;
  public copy(from: Vec2): boolean;
  public copy(a: any, b?: any): boolean {
    const isVec = a instanceof Vec2;
    const xChanged = this.copyX(isVec ? a.x : a);
    const yChanged = this.copyY(isVec ? a.y : b);
    return xChanged || yChanged;
  }

  public setZero(): Vec2 {
    this.set(0, 0);
    return this;
  }

  public setX(x: number): Vec2 {
    // Prohibit change value during animation pending.
    if (this._animationFrameId) return this;
    this._vector[0] = x;
    return this;
  }

  public setY(y: number): Vec2 {
    // Prohibit change value during animation pending.
    if (this._animationFrameId) return this;
    this._vector[1] = y;
    return this;
  }

  public set(x: number, y: number): Vec2;
  public set(vec: Vec2): Vec2;
  public set(a: any, b?: any) {
    const isVec = a instanceof Vec2;
    this.setX(isVec ? a.x : a);
    this.setY(isVec ? a.y : b);
    return this;
  }

  public addX(x: number) {
    this.setX(this.x + x);
    return this;
  }

  public addY(y: number) {
    this.setY(this.y + y);
    return this;
  }

  public add(x: number, y: number): Vec2;
  public add(vec: Vec2): Vec2;
  public add(a: any, b?: any) {
    const isVec = a instanceof Vec2;
    this.addX(isVec ? a.x : a);
    this.addY(isVec ? a.y : b);
    return this;
  }

  public subX(x: number) {
    this.setX(this.x - x);
    return this;
  }

  public subY(y: number) {
    this.setY(this.y - y);
    return this;
  }

  public sub(x: number, y: number): Vec2;
  public sub(vec: Vec2): Vec2;
  public sub(a: any, b?: any) {
    const isVec = a instanceof Vec2;
    this.subX(isVec ? a.x : a);
    this.subY(isVec ? a.y : b);
    return this;
  }

  public multiply(value: number): Vec2;
  public multiply(x: number, y: number): Vec2;
  public multiply(vec: Vec2): Vec2;
  public multiply(a: any, b?: any) {
    const isVec = a instanceof Vec2;
    const x = isVec ? a.x : a;
    const y = isVec ? a.y : b ?? a;
    this.set(this.x * x, this.y * y);
    return this;
  }

  public divide(value: number): Vec2;
  public divide(x: number, y: number): Vec2;
  public divide(vec: Vec2): Vec2;
  public divide(a: any, b?: any) {
    const isVec = a instanceof Vec2;
    const x = isVec ? a.x : a;
    const y = isVec ? a.y : b ?? a;
    this.set(this.x / x, this.y / y);
    return this;
  }

  public swap() {
    const a = this.x;
    const b = this.y;
    this.setX(b);
    this.setY(a);
    return this;
  }

  public clampX(min: number, max: number) {
    this.setX(clamp(this.x, min, max));
    return this;
  }

  public clampY(min: number, max: number) {
    this.setY(clamp(this.y, min, max));
    return this;
  }

  public clamp(v1: Vec2, v2: Vec2) {
    this.clampX(v1.x, v2.x);
    this.clampY(v1.y, v2.y);
    return this;
  }

  public ratioClamp(v1: Vec2, v2: Vec2) {
    const oldX = this.x;
    this.clampX(v1.x, v2.x);
    this.setY(this.y + ((this.x - oldX) * this.y) / oldX);

    const oldY = this.y;
    this.clampY(v1.y, v2.y);
    this.setX(this.x + ((this.y - oldY) * this.x) / oldY);
    return this;
  }

  public negateX(negate?: boolean) {
    this.setX(negation(this.x, negate));
    return this;
  }

  public negateY(negate?: boolean) {
    this.setY(negation(this.y, negate));
    return this;
  }

  public negate(negateX?: boolean, negateY?: boolean): Vec2;
  public negate(negate?: boolean): Vec2;
  public negate(a?: boolean, b?: boolean) {
    const negateX = a;
    const negateY = typeof b === "boolean" ? b : a;
    this.negateX(negateX);
    this.negateY(negateY);
    return this;
  }

  public absX() {
    this.setX(Math.abs(this.x));
    return this;
  }

  public absY() {
    this.setY(Math.abs(this.y));
    return this;
  }

  public abs() {
    this.absX();
    this.absY();
    return this;
  }

  public roundX(decimalPoint?: number) {
    this.setX(round(this.x, decimalPoint));
    return this;
  }

  public roundY(decimalPoint?: number) {
    this.setY(round(this.y, decimalPoint));
    return this;
  }

  public round(decimalPoint?: number) {
    this.roundX(decimalPoint);
    this.roundY(decimalPoint);
    return this;
  }

  public roundToClosestX(closestTo: number) {
    this.setX(roundToClosest(this.x, closestTo));
    return this;
  }

  public roundToClosestY(closestTo: number) {
    this.setY(roundToClosest(this.y, closestTo));
    return this;
  }

  public roundToClosest(closestTo: number) {
    this.roundToClosestX(closestTo);
    this.roundToClosestY(closestTo);
    return this;
  }

  public normalize() {
    const x = this.x;
    const y = this.y;

    let len = x * x + y * y;
    if (len > 0) {
      len = 1 / Math.sqrt(len);
    }

    this.set(this.x * len, this.y * len);
    return this;
  }

  public rotate(origin: Vec2, rad: number) {
    const x = this.x - origin.x;
    const y = this.y - origin.y;

    const sin = Math.sin(rad);
    const cos = Math.cos(rad);

    this.set(x * cos - y * sin + origin.x, x * sin + y * cos + origin.y);

    return this;
  }

  public rotateDegrees(origin: Vec2, degrees: number) {
    this.rotate(origin, toRadians(degrees));
    return this;
  }

  public rotateUnit(rad: number) {
    const sin = Math.sin(rad);
    const cos = Math.cos(rad);
    this.set(this.x * cos - this.y * sin, this.x * sin + this.y * cos);
    return this;
  }

  public rotateUnitDegrees(degrees: number) {
    this.rotateUnit(toRadians(degrees));
    return this;
  }

  public lerp(to: Vec2, ms: number, decimalPoint?: number) {
    this.stopAnimation();
    this._animationFrameId = requestAnimationFrame((now) => {
      this._animate(this, to, ms, now, decimalPoint);
    });
    return this;
  }

  public stopAnimation() {
    cancelAnimationFrame(this._animationFrameId);
    this._animationFrameId = 0;
    this._startTime = 0;
  }

  /* Methods which modify current instance of Vec2 ↑ */

  /* Helpers ↓ */

  public logVector(name?: string) {
    if (name) {
      // eslint-disable-next-line no-console
      console.log(` ${name}: `, "\n", "x: ", this.x, "\n", "y: ", this.y);
    } else {
      // eslint-disable-next-line no-console
      console.log(" x: ", this.x, "\n", "y: ", this.y);
    }
  }

  private _animate(from: Vec2, to: Vec2, by: number, now: number, decimalPoint = 4) {
    if (!this._startTime) this._startTime = now;

    const elapsed = now - this._startTime;

    const t = normalizeClamped(elapsed, 0, by);

    // Important value is set by "_vector" to not go through set accessor
    this._vector[0] = lerpRounded(from.x, to.x, t, decimalPoint);
    this._vector[1] = lerpRounded(from.y, to.y, t, decimalPoint);

    if (elapsed >= by) {
      this.stopAnimation();
      return;
    }

    this._animationFrameId = requestAnimationFrame((now) => {
      this._animate(from, to, by, now, decimalPoint);
    });
  }

  /* Helpers ↑ */

  /* Static logic ↓ */

  public static zero() {
    return new Vec2(0, 0);
  }

  public static zeroHalf() {
    return new Vec2(0, 0.5);
  }

  public static zeroOne() {
    return new Vec2(0, 1);
  }

  public static halfZero() {
    return new Vec2(0.5, 0);
  }

  public static half() {
    return new Vec2(0.5, 0.5);
  }

  public static halfOne() {
    return new Vec2(0.5, 1);
  }

  public static oneZero() {
    return new Vec2(1, 0);
  }
  public static oneHalf() {
    return new Vec2(1, 0.5);
  }

  public static one() {
    return new Vec2(1, 1);
  }

  public static X(x: number) {
    return new Vec2(x, 0);
  }

  public static W(width: number) {
    return new Vec2(width, 0);
  }

  public static Y(y: number) {
    return new Vec2(0, y);
  }

  public static H(height: number) {
    return new Vec2(0, height);
  }
}

export default Vec2;
